import * as React from 'react';
import { z } from 'zod';
import { Plus, X } from 'lucide-react';
import { Avatar } from './Avatar';
import { Button } from './Button';
import { Card, CardContent, CardHeader } from './Card';
import { Label } from './Field';
import { Icon } from './Icon';
import { Form, FormField, useZodForm } from './Form';
import { Input } from './Input';
import { Inline, Stack } from './Layout';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './Select';
import { Text } from './Text';

export interface EntityPickerOption {
  value: string;
  label: React.ReactNode;
  /** A second line under the label in the list. */
  description?: React.ReactNode;
  /** A picture before the label; `avatarFallback` stands in without one. */
  avatarUrl?: string | null;
  avatarFallback?: string;
  /** A tag after the label — a role, a status. */
  badge?: React.ReactNode;
}

export interface EntityPickerProps {
  label: React.ReactNode;
  /** The control's id, so the label reaches it. */
  id?: string;
  options: EntityPickerOption[];
  /** The chosen option's value; empty or undefined for none yet. */
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Draws the marker after the label, as `Field` does. */
  required?: boolean;
  /** Draws the trigger in the destructive tone. */
  error?: boolean;
  /** The list shows `loadingLabel` as a disabled row instead of the options. */
  loading?: boolean;
  loadingLabel?: React.ReactNode;
  /** The disabled row shown when there are no options. */
  emptyLabel?: React.ReactNode;
  /** A quiet line under the control. */
  hint?: React.ReactNode;
  /**
   * Offers a new entry. `action` is a button beside the label; `option` is
   * the first row of the list. Drawn only when `onCreate` is given.
   */
  createLabel?: React.ReactNode;
  createPlacement?: 'action' | 'option';
  onCreate?: () => void;
  /**
   * Create mode: `createForm` — or, without one, a single-name composer that
   * submits through `onCreateSubmit` — replaces the select. The container
   * owns the flag so it can close the form when the save lands.
   */
  creating?: boolean;
  /** The label while creating. Defaults to `label`. */
  creatingLabel?: React.ReactNode;
  /** Draws an X beside the creating label. */
  onCancelCreate?: () => void;
  createForm?: React.ReactNode;
  /** Wraps `createForm` in a card headed with this. */
  createTitle?: React.ReactNode;
  createPlaceholder?: string;
  createPending?: boolean;
  onCreateSubmit?: (name: string) => void;
}

/** The sentinel the "create" row carries; never reaches `onChange`. */
const CREATE_VALUE = '__create__';

/**
 * Pick one of a list of things that live on the server — an account, a lot,
 * a company — with, optionally, a way to add a new one without leaving the
 * field.
 *
 *   <EntityPicker
 *     label="Account"
 *     options={accounts}
 *     value={accountId}
 *     onChange={setAccountId}
 *     loading={isLoading}
 *     loadingLabel="Loading accounts..."
 *     emptyLabel="No accounts available"
 *     createLabel="Add Account"
 *     onCreate={() => setCreating(true)}
 *     creating={creating}
 *     creatingLabel="New Account Name"
 *     onCancelCreate={() => setCreating(false)}
 *     onCreateSubmit={createAccount}
 *   />
 *
 * Options with an avatar, a description or a badge draw as rich rows; plain
 * ones as text. The chosen option's avatar and badge show in the trigger.
 */
export function EntityPicker({
  label,
  id,
  options,
  value,
  onChange,
  placeholder = 'Select…',
  required = false,
  error = false,
  loading = false,
  loadingLabel = 'Loading…',
  emptyLabel = 'No options',
  hint,
  createLabel = 'Create new',
  createPlacement = 'action',
  onCreate,
  creating = false,
  creatingLabel,
  onCancelCreate,
  createForm,
  createTitle,
  createPlaceholder = 'Enter a name',
  createPending = false,
  onCreateSubmit,
}: EntityPickerProps) {
  if (creating) {
    const form = createForm ?? (
      <NameComposer
        placeholder={createPlaceholder}
        pending={createPending}
        onSubmit={(name) => onCreateSubmit?.(name)}
      />
    );
    return (
      <Stack gap={2}>
        <Inline gap={0} align="center" justify="between">
          <Label required={required}>{creatingLabel ?? label}</Label>
          {onCancelCreate ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancelCreate}
              aria-label="Cancel"
            >
              <Icon icon={X} />
            </Button>
          ) : null}
        </Inline>
        {createTitle ? (
          <Card>
            <CardHeader title={createTitle} />
            <CardContent>{form}</CardContent>
          </Card>
        ) : (
          form
        )}
      </Stack>
    );
  }

  const selected = options.find((option) => option.value === value);
  const rich = (option: EntityPickerOption) =>
    option.avatarUrl !== undefined ||
    option.avatarFallback !== undefined ||
    option.badge !== undefined ||
    option.description !== undefined;

  return (
    <Stack gap={2}>
      <Inline gap={0} align="center" justify="between">
        <Label htmlFor={id} required={required}>
          {label}
        </Label>
        {onCreate && createPlacement === 'action' ? (
          <Button variant="ghost" size="sm" onClick={onCreate}>
            <Icon icon={Plus} />
            {createLabel}
          </Button>
        ) : null}
      </Inline>
      <Select
        value={value}
        onValueChange={(next) => {
          if (next === CREATE_VALUE) {
            onCreate?.();
            return;
          }
          onChange(next);
        }}
        required={required}
      >
        <SelectTrigger id={id} aria-invalid={error ? true : undefined}>
          <SelectValue placeholder={placeholder}>
            {selected && rich(selected) ? (
              <Inline gap={2} align="center" justify="between">
                <Inline gap={2} align="center" grow>
                  {selected.avatarUrl !== undefined ||
                  selected.avatarFallback !== undefined ? (
                    <Avatar
                      size="xs"
                      src={selected.avatarUrl}
                      fallback={selected.avatarFallback}
                    />
                  ) : null}
                  <Text as="span" variant="inherit" truncate>
                    {selected.label}
                  </Text>
                </Inline>
                {selected.badge}
              </Inline>
            ) : undefined}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {loading ? (
            <SelectItem value="loading" disabled>
              {loadingLabel}
            </SelectItem>
          ) : (
            <>
              {onCreate && createPlacement === 'option' ? (
                <SelectItem value={CREATE_VALUE}>
                  <Inline as="span" gap={2} align="center">
                    <Icon icon={Plus} size="sm" tone="primary" />
                    {createLabel}
                  </Inline>
                </SelectItem>
              ) : null}
              {options.length > 0 ? (
                options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {rich(option) ? (
                      <Inline gap={2} align="center" className="w-full">
                        {option.avatarUrl !== undefined ||
                        option.avatarFallback !== undefined ? (
                          <Avatar
                            size="xs"
                            src={option.avatarUrl}
                            fallback={option.avatarFallback}
                          />
                        ) : null}
                        <Stack gap={0} grow>
                          <Text variant="body-sm-strong">{option.label}</Text>
                          {option.description ? (
                            <Text variant="caption" tone="muted">
                              {option.description}
                            </Text>
                          ) : null}
                        </Stack>
                        {option.badge}
                      </Inline>
                    ) : (
                      option.label
                    )}
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="empty" disabled>
                  {emptyLabel}
                </SelectItem>
              )}
            </>
          )}
        </SelectContent>
      </Select>
      {hint ? (
        <Text variant="caption" tone="muted">
          {hint}
        </Text>
      ) : null}
    </Stack>
  );
}

interface NameComposerProps {
  placeholder: string;
  pending: boolean;
  onSubmit: (name: string) => void;
}

const nameComposerSchema = z.object({
  name: z.string().trim().min(1, 'Enter a name.'),
});

/**
 * A name and a Create button — the whole form for things that are only a
 * name. Empty, Create says so under the field; the containers used to raise
 * a toast from their own handler, which is a long way from the box.
 */
function NameComposer({ placeholder, pending, onSubmit }: NameComposerProps) {
  const form = useZodForm(nameComposerSchema, { defaultValues: { name: '' } });
  return (
    <Form form={form} gap={2} onSubmit={(values) => onSubmit(values.name)}>
      <Inline gap={2} align="start">
        <Stack gap={0} grow>
          <FormField form={form} name="name">
            {(field) => <Input {...field} placeholder={placeholder} />}
          </FormField>
        </Stack>
        <Button type="submit" disabled={pending}>
          {pending ? 'Creating...' : 'Create'}
        </Button>
      </Inline>
    </Form>
  );
}
